export const WORKER_READINESS_LADDER = [
  'LOGIN',
  'ONBOARDING_IN_PROGRESS',
  'BLOCKING_STEPS_CLEAR',
  'ALL_REQUIRED_CLEAR',
  'READY_FOR_FIRST_SHIFT',
] as const;

export type WorkerReadinessStage = (typeof WORKER_READINESS_LADDER)[number];

export const WORKER_READINESS_STAGE_LABELS: Record<WorkerReadinessStage, string> = {
  LOGIN: 'Login readiness',
  ONBOARDING_IN_PROGRESS: 'Onboarding in progress',
  BLOCKING_STEPS_CLEAR: 'Assignable',
  ALL_REQUIRED_CLEAR: 'First-shift gates clear',
  READY_FOR_FIRST_SHIFT: 'Ready for first shift',
};

export const WORKER_READINESS_BLOCKER_CODES = [
  'LOGIN_EMAIL_REQUIRED',
  'LOGIN_INVITE_REQUIRED',
  'LOGIN_LINK_REQUIRED',
  'LOGIN_REVIEW_REQUIRED',
  'ONBOARDING_JOURNEY_NOT_STARTED',
  'ONBOARDING_JOURNEY_NOT_COMPLETED',
  'BLOCKING_ONBOARDING_STEPS_INCOMPLETE',
  'REQUIRED_ONBOARDING_STEPS_INCOMPLETE',
  'ROSTERING_NOT_ACTIVE',
  'ROLE_CERTIFICATION_MISSING',
  'ENFORCED_POLICY_ACKNOWLEDGEMENT_REQUIRED',
] as const;

export type WorkerReadinessBlockerCode = (typeof WORKER_READINESS_BLOCKER_CODES)[number];

export const WORKER_READINESS_BLOCKER_LABELS: Record<WorkerReadinessBlockerCode, string> = {
  LOGIN_EMAIL_REQUIRED: 'Worker email required',
  LOGIN_INVITE_REQUIRED: 'Login invite required',
  LOGIN_LINK_REQUIRED: 'Existing login needs linking',
  LOGIN_REVIEW_REQUIRED: 'Account match needs review',
  ONBOARDING_JOURNEY_NOT_STARTED: 'Onboarding journey not started',
  ONBOARDING_JOURNEY_NOT_COMPLETED: 'Onboarding journey not completed',
  BLOCKING_ONBOARDING_STEPS_INCOMPLETE: 'Blocking onboarding steps incomplete',
  REQUIRED_ONBOARDING_STEPS_INCOMPLETE: 'Required onboarding steps incomplete',
  ROSTERING_NOT_ACTIVE: 'Not active for rostering',
  ROLE_CERTIFICATION_MISSING: 'Role certification missing',
  ENFORCED_POLICY_ACKNOWLEDGEMENT_REQUIRED: 'Enforced policy acknowledgement required',
};

export const WORKER_READINESS_GATES = [
  {
    code: 'W_01_LOGIN',
    label: 'Login linked',
    stage: 'LOGIN',
  },
  {
    code: 'W_05_ASSIGNABLE',
    label: 'Blocking steps clear for assignment',
    stage: 'BLOCKING_STEPS_CLEAR',
  },
  {
    code: 'W_06_FIRST_SHIFT',
    label: 'All required steps and rostering active',
    stage: 'ALL_REQUIRED_CLEAR',
  },
  {
    code: 'W_07_ROLE_CERTIFICATION',
    label: 'Role certifications current',
    stage: 'READY_FOR_FIRST_SHIFT',
    independent: true,
  },
  {
    code: 'W_08_POLICY_ACK',
    label: 'Required policies acknowledged',
    stage: 'READY_FOR_FIRST_SHIFT',
  },
] as const;

export type WorkerReadinessGateCode = (typeof WORKER_READINESS_GATES)[number]['code'];

const WORKER_READINESS_STAGE_SET: ReadonlySet<string> = new Set(WORKER_READINESS_LADDER);
const UI_TERMINAL_STEP_STATUSES = new Set(['COMPLETED', 'VERIFIED', 'WAIVED', 'CANCELLED']);
const GATE_SATISFIED_WORKFLOW_STATUSES = new Set(['COMPLETED', 'CANCELLED']);

export type WorkerOnboardingProgressStep = {
  status?: string | null;
  is_blocking_for_first_shift?: boolean | null;
  isBlockingForFirstShift?: boolean | null;
  metadata?: Record<string, unknown> | null;
};

export type WorkerOnboardingProgressInput = {
  steps: readonly WorkerOnboardingProgressStep[];
  isActiveForRostering?: boolean | null;
  journeyStatus?: string | null;
};

export type WorkerOnboardingProgress = {
  totalSteps: number;
  gateSatisfiedSteps: number;
  displayTerminalSteps: number;
  progressPercent: number;
  displayTerminalPercent: number;
  blockingStepsOutstanding: number;
  firstShiftBlockersOutstanding: number;
  isAssignable: boolean;
  isFirstShiftReady: boolean;
};

function normalizeStatus(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function metadataOnboardingStepStatus(step: WorkerOnboardingProgressStep): string {
  const metadata = step.metadata ?? {};
  const snake = metadata.onboarding_step_status;
  const camel = metadata.onboardingStepStatus;
  return normalizeStatus(
    typeof snake === 'string' ? snake : typeof camel === 'string' ? camel : undefined,
  );
}

function isBlockingForFirstShift(step: WorkerOnboardingProgressStep): boolean {
  return step.is_blocking_for_first_shift === true || step.isBlockingForFirstShift === true;
}

export function isWorkerReadinessStage(
  status: string | null | undefined,
): status is WorkerReadinessStage {
  return typeof status === 'string' && WORKER_READINESS_STAGE_SET.has(status);
}

export function workerReadinessStageLabel(status: string | null | undefined): string {
  if (!isWorkerReadinessStage(status)) return 'Unknown worker readiness stage';
  return WORKER_READINESS_STAGE_LABELS[status];
}

export function workerReadinessBlockerLabel(code: string | null | undefined): string {
  if (
    typeof code === 'string' &&
    WORKER_READINESS_BLOCKER_CODES.includes(code as WorkerReadinessBlockerCode)
  ) {
    return WORKER_READINESS_BLOCKER_LABELS[code as WorkerReadinessBlockerCode];
  }
  return 'Readiness blocker';
}

export function isWorkerOnboardingStepTerminalForDisplay(
  step: WorkerOnboardingProgressStep,
): boolean {
  const status = normalizeStatus(step.status);
  const metadataStatus = metadataOnboardingStepStatus(step);
  return UI_TERMINAL_STEP_STATUSES.has(status) || UI_TERMINAL_STEP_STATUSES.has(metadataStatus);
}

/**
 * True when a step has been waived or cancelled and therefore no longer requires
 * its declared evidence — that is what a waiver means.
 *
 * The document-requirement projection is a pure function of the step's declared
 * categories and the documents that exist, so it keeps producing NOT_ISSUED rows
 * for a waived step forever. Every consumer that treats "requirement unresolved"
 * as a blocker must therefore exclude waived steps, or the waiver can never take
 * effect and no actor can clear it.
 *
 * Observed on Golden Bloom 2026-08-07: journeys whose every workflow_task was
 * terminal still rendered "Completion is blocked until every unresolved
 * requirement has an owner and an explicit next action", and "Mark ready" stayed
 * hidden. Waiving could not clear it and neither could the provider.
 *
 * COMPLETED is deliberately excluded — an unresolved requirement on a genuinely
 * completed step is a real reconciliation defect and must stay visible.
 *
 * Reads both the `v_worker_onboarding_steps` row status and the raw
 * `workflow_tasks` metadata, because callers receive one or the other depending
 * on the query, and a predicate that saw only one source would silently miss half
 * the cases.
 */
export function isWorkerOnboardingStepWaivedForRequirements(
  step: WorkerOnboardingProgressStep,
): boolean {
  const status = normalizeStatus(step.status);
  const metadataStatus = metadataOnboardingStepStatus(step);
  return (
    status === 'WAIVED' ||
    metadataStatus === 'WAIVED' ||
    status === 'CANCELLED' ||
    metadataStatus === 'CANCELLED'
  );
}

export function isWorkerOnboardingStepSatisfiedForGate(
  step: WorkerOnboardingProgressStep,
): boolean {
  const status = normalizeStatus(step.status);
  if (GATE_SATISFIED_WORKFLOW_STATUSES.has(status)) return true;
  // WAIVED can arrive as the v_worker_onboarding_steps row status or as raw
  // workflow_tasks metadata, so keep both sources aligned with the SQL gates.
  return metadataOnboardingStepStatus(step) === 'WAIVED' || status === 'WAIVED';
}

export function computeWorkerOnboardingProgress(
  input: WorkerOnboardingProgressInput,
): WorkerOnboardingProgress {
  const totalSteps = input.steps.length;
  const gateSatisfiedSteps = input.steps.filter(isWorkerOnboardingStepSatisfiedForGate).length;
  const displayTerminalSteps = input.steps.filter(isWorkerOnboardingStepTerminalForDisplay).length;
  const firstShiftBlockersOutstanding = input.steps.filter(
    (step) => !isWorkerOnboardingStepSatisfiedForGate(step),
  ).length;
  const blockingStepsOutstanding = input.steps.filter(
    (step) => isBlockingForFirstShift(step) && !isWorkerOnboardingStepSatisfiedForGate(step),
  ).length;
  const isActiveForRostering = input.isActiveForRostering === true;
  const journeyCompleted =
    input.journeyStatus == null
      ? firstShiftBlockersOutstanding === 0
      : normalizeStatus(input.journeyStatus) === 'COMPLETED';

  return {
    totalSteps,
    gateSatisfiedSteps,
    displayTerminalSteps,
    progressPercent: totalSteps > 0 ? Math.round((gateSatisfiedSteps / totalSteps) * 100) : 0,
    displayTerminalPercent:
      totalSteps > 0 ? Math.round((displayTerminalSteps / totalSteps) * 100) : 0,
    blockingStepsOutstanding,
    firstShiftBlockersOutstanding,
    isAssignable: isActiveForRostering && blockingStepsOutstanding === 0,
    isFirstShiftReady:
      journeyCompleted && isActiveForRostering && firstShiftBlockersOutstanding === 0,
  };
}
